/**
 * Confirmation Modal Component
 * Replaces browser confirm() with beautiful, customizable modal dialogs
 *
 * Usage:
 * import { showConfirm } from './components/ConfirmModal';
 *
 * const confirmed = await showConfirm({
 *   title: "Delete item?",
 *   message: "This action cannot be undone.",
 *   confirmText: "Delete",
 *   type: "danger"
 * });
 *
 * if (confirmed) {
 *   // User clicked confirm
 * }
 */

import React, { createContext, useContext, useState, useCallback } from "react";
import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiInfo } from "react-icons/fi";
import "./ConfirmModal.css";

const ConfirmContext = createContext();

export const ConfirmProvider = ({ children }) => {
  const [modal, setModal] = useState(null);

  const showConfirm = useCallback((options) => {
    return new Promise((resolve) => {
      setModal({
        ...options,
        onConfirm: () => {
          setModal(null);
          resolve(true);
        },
        onCancel: () => {
          setModal(null);
          resolve(false);
        },
      });
    });
  }, []);

  return (
    <ConfirmContext.Provider value={{ showConfirm }}>
      {children}
      {modal && <ConfirmModal {...modal} />}
    </ConfirmContext.Provider>
  );
};

const ConfirmModal = ({
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "default", // default, danger, warning, success
  onConfirm,
  onCancel,
}) => {
  const IconComponent = {
    default: FiInfo,
    danger: FiAlertTriangle,
    warning: FiAlertCircle,
    success: FiCheckCircle,
  }[type] || FiInfo;

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onCancel]);

  return (
    <div className="confirm-overlay" onClick={onCancel}>
      <div
        className="confirm-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
      >
        <div className={`confirm-icon confirm-icon-${type}`}>
          <IconComponent size={32} strokeWidth={2} aria-hidden />
        </div>

        <h2 id="confirm-title" className="confirm-title">
          {title}
        </h2>
        <p id="confirm-message" className="confirm-message">
          {message}
        </p>

        <div className="confirm-actions">
          <button
            type="button"
            className="confirm-btn confirm-btn-cancel"
            onClick={onCancel}
            autoFocus
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`confirm-btn confirm-btn-confirm confirm-btn-${type}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook to use confirm in components
export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within ConfirmProvider");
  }
  return context;
};

// Global instance for use outside React components
let globalConfirmContext = null;

export const initializeConfirm = (context) => {
  globalConfirmContext = context;
};

export const showConfirm = async (options) => {
  if (!globalConfirmContext) {
    console.warn("ConfirmProvider not initialized; confirm denied.");
    return false;
  }
  return globalConfirmContext.showConfirm(options);
};

export default ConfirmProvider;
